import type {CustomDealProduct} from '../types';
import type {PageProps} from '@router';

import {SearchOutlined, ShoppingCartOutlined} from '@ant-design/icons';
import {Button, Chip, Loading, Tabs} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {toast} from '@Toast';
import Empty from 'common/empty/Empty';
import {useCallback, useEffect, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {
  addBulkProductsToDraftDeal,
  addProductToTop,
  addSearchedProduct,
  customDealsCats,
  customDealsSubCats,
  getProducts,
  resetCategories,
  resetProducts,
  resetSubCategories
} from 'store/slices/custom-deal';
import {NumberParam, StringParam, useQueryParams, withDefault} from 'use-query-params';

import {CUSTOM_DEAL_PAGE_SIZE, CUSTOM_DEAL_ROUTES} from '../constants';
import {getCustomDealVendorId} from '../utils';
import CategoriesSkeleton from './components/CategoriesSkeleton/CategoriesSkeleton';
import ProductSearchOverlay from './components/ProductSearchOverlay/ProductSearchOverlay';
import SelectProductsFilters from './components/SelectProductsFilters/SelectProductsFilters';
import SelectProductsListItem from './components/SelectProductsListItem/SelectProductsListItem';
import SelectProductsListItemSkeleton from './components/SelectProductsListItemSkeleton/SelectProductsListItemSkeleton';
import SubCategoriesSkeleton from './components/SubCategoriesSkeleton/SubCategoriesSkeleton';

const SelectProducts: React.FC<PageProps> = ({headerData}) => {
  const [showProductSearchOverlay, setShowProductSearchOverlay] = useState(false);
  const [query, setQuery] = useQueryParams({
    selectedCategory: withDefault(NumberParam, null),
    selectedSubCategory: withDefault(NumberParam, null),
    search: withDefault(StringParam, null),
    stock_status: withDefault(StringParam, null),
    sort: withDefault(StringParam, null),
    badge: withDefault(StringParam, null),
    ptk: withDefault(StringParam, null)
  });
  const selectedCategory = query.selectedCategory;
  const selectedSubCategory = query.selectedSubCategory;
  const setSelectedCategory = useCallback(
    (value: number | null) => {
      setQuery({selectedCategory: value}, 'replaceIn');
    },
    [setQuery]
  );
  const setSelectedSubCategory = useCallback(
    (value: number | null) => {
      setQuery({selectedSubCategory: value}, 'replaceIn');
    },
    [setQuery]
  );
  const [pagination, setPagination] = useState({page: 1, pageSize: CUSTOM_DEAL_PAGE_SIZE});
  const [selectedProductIds, setSelectedProductIds] = useState(new Set<string | number>());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const vendorId = getCustomDealVendorId();
  const {
    categories,
    categoriesLoading,
    subCategories,
    subCategoriesLoading,
    products,
    productsLoading,
    searchedProducts
  } = useAppSelector((state) => state.customDeal);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    headerData?.({
      icon: <SearchOutlined aria-hidden />,
      iconButtonSize: 'lg',
      action: () => {
        setShowProductSearchOverlay(true);
      }
    });
    dispatch(resetCategories());
    dispatch(resetSubCategories());
    void dispatch(customDealsCats({vendor_id: vendorId}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let firstCategoryId: number | null = null;
    if (categories?.length > 0 && !selectedCategory) {
      firstCategoryId = categories[0].id;
      if (!firstCategoryId) {
        return;
      }
      setSelectedCategory(firstCategoryId);
    }
    void dispatch(
      customDealsSubCats({vendor_id: vendorId, parent_id: firstCategoryId || selectedCategory})
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  useEffect(() => {
    if (subCategories?.length > 0 && !selectedSubCategory) {
      setSelectedSubCategory(subCategories[0].id);
    }
  }, [subCategories, selectedSubCategory, setSelectedSubCategory]);

  useEffect(() => {
    if (!selectedSubCategory) {
      return;
    }
    setPagination((current) => ({...current, page: 1}));
    dispatch(resetProducts());
    void dispatch(
      getProducts({
        vendor_id: vendorId,
        category_id: selectedSubCategory,
        page_size: pagination.pageSize,
        page: 1,
        search: query.search,
        stock_status: query.stock_status,
        sort: query.sort,
        badge: query.badge,
        ptk: query.ptk
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubCategory, query.search, query.stock_status, query.sort, query.badge, query.ptk]);

  useEffect(() => {
    if (productsLoading || !searchedProducts.length || !selectedSubCategory || !selectedCategory) {
      return;
    }

    searchedProducts.forEach((product: CustomDealProduct) => {
      if (
        product.parent_category_id === selectedCategory &&
        product.category_id === selectedSubCategory
      ) {
        dispatch(addProductToTop(product as never));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsLoading]);

  const loadMore = () => {
    void dispatch(
      getProducts({
        vendor_id: vendorId,
        page_size: pagination.pageSize,
        page: pagination.page + 1,
        search: query.search,
        stock_status: query.stock_status,
        sort: query.sort,
        badge: query.badge,
        ptk: query.ptk,
        category_id: selectedSubCategory
      })
    );
    setPagination({pageSize: pagination.pageSize, page: pagination.page + 1});
  };

  const handleProductSelection = (item: CustomDealProduct) => {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      if (item.selected) {
        next.add(item.id);
      } else {
        next.delete(item.id);
      }
      return next;
    });
  };

  const handleProductSelectFromSearch = (product: CustomDealProduct) => {
    dispatch(addSearchedProduct(product as never));
    if (
      product.parent_category_id === selectedCategory &&
      product.category_id === selectedSubCategory
    ) {
      dispatch(addProductToTop(product as never));
    }
    setSelectedProductIds((current) => new Set([...current, product.id]));
  };

  const handleProductDeselectFromSearch = (product: CustomDealProduct) => {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      next.delete(product.id);
      return next;
    });
  };

  const handleSubmitSelectedProducts = async () => {
    if (!selectedProductIds.size || isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);
      const response = (await dispatch(
        addBulkProductsToDraftDeal({
          vendorId,
          productIds: Array.from(selectedProductIds)
        })
      ).unwrap()) as {errors?: {message?: string}; data?: {deal_id?: string | number}};

      if (response?.errors?.message) {
        toast('خطا در ثبت محصولات');
        return;
      }

      if (response?.data?.deal_id) {
        navigate(
          CUSTOM_DEAL_ROUTES.applyDiscount.replace(':dealId', String(response.data.deal_id))
        );
        return;
      }

      toast('پاسخ نامعتبر از سرور دریافت شد');
    } catch (error: unknown) {
      const requestError = error as {errors?: {message?: string}; message?: string};
      toast(requestError.errors?.message || requestError.message || 'خطایی در برقراری ارتباط با سرور رخ داد');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    setQuery({selectedCategory: Number(categoryId), selectedSubCategory: null}, 'replaceIn');
    dispatch(resetSubCategories());
    void dispatch(customDealsSubCats({vendor_id: vendorId, parent_id: Number(categoryId)}));
  };

  if (!categories?.length && !categoriesLoading) {
    return (
      <div className="flex min-h-50 flex-1 items-center justify-center">
        <Empty>
          <p>هیچ کالایی برای نمایش وجود ندارد</p>
        </Empty>
      </div>
    );
  }

  return (
    <>
      <ProductSearchOverlay
        show={showProductSearchOverlay}
        close={() => setShowProductSearchOverlay(false)}
        onProductSelect={handleProductSelectFromSearch}
        onProductDeselect={handleProductDeselectFromSearch}
        selectedProductIds={selectedProductIds}
      />
      <PageLayout
        id="selectProductsListWrapper"
        header={
          <>
            <SelectProductsFilters
              filters={query}
              onChangeFilter={(name, value) => {
                setQuery((current) => ({...current, [name]: value}), 'replace');
              }}
            />
            {categoriesLoading ? (
              <div className="px-4 py-2">
                <CategoriesSkeleton />
              </div>
            ) : categories?.length > 0 ? (
              <section className="max-w-full py-2">
                <Tabs
                  activeKey={selectedCategory?.toString()}
                  onChange={handleCategoryChange}
                  items={categories.map((category: {id: number; title: string}) => ({
                    key: category.id.toString(),
                    label: category.title
                  }))}
                  tabBarClassName="mb-2"
                />
                {selectedCategory ? (
                  <div className="flex overflow-x-auto px-4 scrollbar-none">
                    {subCategoriesLoading ? (
                      <SubCategoriesSkeleton />
                    ) : subCategories?.length > 0 ? (
                      <div className="flex w-max items-center gap-2">
                        {subCategories.map((subCategory: {id: number; title: string}) => (
                          <Chip
                            key={subCategory.id}
                            label={subCategory.title}
                            selected={selectedSubCategory === subCategory.id}
                            onClick={() => setSelectedSubCategory(subCategory.id)}
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </section>
            ) : null}
          </>
        }
        footer={
          selectedProductIds.size > 0 ? (
            <div className="flex w-full flex-col gap-2">
              <div className="mb-2 flex items-center gap-1">
                <ShoppingCartOutlined className="text-body-lg" aria-hidden />
                <span className="text-body-sm">{`${selectedProductIds.size} کالا انتخاب شده`}</span>
              </div>
              <Button
                fullWidth
                size="lg"
                loading={isSubmitting}
                onClick={() => void handleSubmitSelectedProducts()}
              >
                ادامه
              </Button>
            </div>
          ) : undefined
        }
        footerClassName="min-h-28.75 rounded-t-2xl px-4 py-2 shadow-[0_-4px_12px_rgb(0_0_0_/_0.1)]"
      >
        {!productsLoading && products.items?.length === 0 ? (
          <div className="flex min-h-50 flex-1 items-center justify-center">
            <Empty>
              <p>هیچ کالایی برای نمایش وجود ندارد</p>
            </Empty>
          </div>
        ) : null}
        <div className="flex flex-col">
          <InfiniteScroll
            scrollableTarget="selectProductsListWrapper"
            scrollThreshold={0.8}
            pullDownToRefreshContent={<></>}
            pullDownToRefreshThreshold={50}
            next={loadMore}
            hasMore={products?.items?.length < products?.total_items}
            loader={
              <div className="flex h-10 justify-center">
                <Loading size="medium" color="primary" />
              </div>
            }
            dataLength={products?.items?.length || 0}
          >
            {productsLoading && (!products?.items || products.items.length === 0) ? (
              Array.from({length: 5}).map((_, index) => (
                <SelectProductsListItemSkeleton key={`skeleton-${index}`} />
              ))
            ) : (
              products?.items?.map((item: CustomDealProduct) => (
                <SelectProductsListItem
                  key={item.id}
                  item={item}
                  selected={selectedProductIds.has(item.id)}
                  handleProductSelection={handleProductSelection}
                />
              ))
            )}
          </InfiniteScroll>
        </div>
      </PageLayout>
    </>
  );
};

export default SelectProducts;
